import React from "react";

import InputLabel from "components/inputfields/InputLabel";
import MultipleSelect from "components/inputfields/MultipleSelect";
import InputTextField from "components/inputfields/InputTextField";

import IProduct from "models/product";
import { animalCategoriesForFeed } from "../../../constants-ts";
import { IFoodlaCategoryOption } from "models/category";
import ThreeWaySwitch from "components/inputfields/ThreeWaySwitch";
import useDebouncedStateString from "hooks/useDebouncedState";

interface IAnimalFieldsDataProps {
  readOnly?: boolean;
  approve: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  rootCategory?: IFoodlaCategoryOption;
};

const AnimalFields = ({ readOnly, approve, state, setState, rootCategory }: IAnimalFieldsDataProps) => {
  const [responsibleLabelingApprovalNumber, handleResponsibleLabelingApprovalNumberChange] = useDebouncedStateString(
    state.responsibleLabelingApprovalNumber || '',
    setState,
    'responsibleLabelingApprovalNumber',
    300
  );

  const handleDropdownChange = (
    event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>,
  ) => {
      const stateName = event.target.name;
      const stateValue = event.target.value;

      const val = !stateValue || stateValue === '' ? null : stateValue;
      stateName && setState({ ...state, [stateName]: val });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;
    
    setState({ ...state, [name]: value });
  };

  return (
    <div style={{width: '100%'}}>
      <InputLabel
        heading="Godkännandenummer ansvarig för märkning eller ansvarig för märkning"
        text="Om personen som ansvarar för märkningen har flera godkännandenummer ska det nummer som tilldelats i enlighet med förordning (EG) nr 183/2005 användas."
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="responsibleLabelingApprovalNumber"
        value={responsibleLabelingApprovalNumber}
        placeholder="Godkännandenummer för den person som ansvarar för märkningen"
        onChange={(e) => handleResponsibleLabelingApprovalNumberChange(e.target.value)}
        approve={approve && !!state?.approve?.find(x => x.field === 'responsibleLabelingApprovalNumber')}
      />
      <InputLabel
        heading="Djurkategori för foder"
        text="Välj vilken djurart eller djurkategori(er) som foderråvaran är avsedd för. Detta är ett märkningskrav för foder enligt förordning (EG) 776/2009."
        link="https://eur-lex.europa.eu/legal-content/SV/TXT/?uri=celex%3A32009R0767"
      />
      <div style={{ display: 'flex', width: '100%' }}>
        <MultipleSelect
          readOnly={readOnly}
          disableLabel={true}
          listData={animalCategoriesForFeed}
          value={state?.animalCategoriesForFeed ?? []}
          targetName="animalCategoriesForFeed"
          handleChange={handleDropdownChange}
          style={{ flexGrow: 1, overflow: 'hidden' }}
        />
        {approve && state?.approve?.find(x => x.field === 'animalCategoriesForFeed') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            style={{ height: 56 }}
            setState={setState}
            name="animalCategoriesForFeed"
          />
        )}
      </div>
      <InputLabel
        heading="Ålderskategori"
        text="Den ålderskategori som fodret är anpassat för. Läs mer om kraven på sidan 124."
        link="https://gs1.se/wp-content/uploads/sites/2/2023/08/handledning-artikelinformation_3.1.24_1_1.pdf"
      />
      <InputTextField
        readOnly={readOnly}
        state={state}
        setState={setState}
        inputName="animalAgeGroup"
        value={state.animalAgeGroup}
        placeholder="Ex. vuxen, 5-8 år"
        onChange={handleChange}
        approve={approve && !!state?.approve?.find(x => x.field === 'animalAgeGroup')}
      />
    </div>
  );
}

export default AnimalFields;
